import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { KLASSEN_LABELS } from "./data";
import {
  SAMMELGRID_KLASSEN,
  type SammelGridKlassenDef,
  type SammelGridState,
  type SammelGridSubklasse,
} from "./SammelvertragGrid";
import type { KlasseKey, RiskSummary } from "./types";

/**
 * Zuordnungs-Tabelle für den Versicherungsumfang im Sammelvertrag.
 *
 *  Eine eigene Tabelle pro Klasse (nur wenn Fahrzeuge in der Klasse).
 *
 *  Spalten = Varianten der Klasse:
 *    - "Parent" (z. B. "1") aus der Klassen-Definition + Subklassen ("1a", "1b").
 *    Jede Variante entspricht einer gültigen Kombination:
 *      KH alleine  |  KH + TK (eine SB)  |  KH + VK/TK (eine SB-Kombi)
 *
 *  Header (dreizeilig pro Tabelle):
 *    1. Klasse-Label · Variant-Tag (1, 1a, 1b …)
 *    2. Klasse-Label · Combo-Typ (KH | KH + TK | KH + VK/TK)
 *    3. Klasse-Label · SB-Wert aus der Vereinbarung (— für KH-only)
 *
 *  Body:
 *    - "Für alle Fahrzeuge" Zeile mit Radios je Variante + Gesamt-Beitrag
 *      (Variant-Beitrag × Anzahl Fahrzeuge der Klasse).
 *    - Eine Zeile pro Fahrzeug mit Radios je Variante + Variant-Beitrag.
 */
type ComboType = "kh" | "kh+tk" | "kh+vktk";

type Variant = {
  /** "parent" oder Subklassen-Buchstabe ("a", "b" …). */
  id: string;
  /** Anzeige-Label, z. B. "1" für Parent, "1a" für sub. */
  label: string;
  comboType: ComboType;
  sb: string | null;
  beitrag: number;
};

const COMBO_LABEL: Record<ComboType, string> = {
  kh: "KH",
  "kh+tk": "KH + TK",
  "kh+vktk": "KH + VK/TK",
};

const COMBO_COLOR: Record<ComboType, string> = {
  kh: "#3D8BD9",
  "kh+tk": "#E66A2C",
  "kh+vktk": "#2E7D4F",
};

const parseEur = (raw: string | undefined): number => {
  if (!raw) return 0;
  const n = parseFloat(raw.replace(/\s/g, "").replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

const formatEur = (n: number): string =>
  `${n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const variantFromSlot = (
  id: string,
  label: string,
  slot: { selection: Record<string, true>; beitraege: Record<string, string> },
): Variant | null => {
  const selKeys = Object.keys(slot.selection);
  if (selKeys.length === 0) return null;
  const hasKh = !!slot.selection["kh"];
  const tkKey = selKeys.find((k) => k.startsWith("tk:"));
  const vktkKey = selKeys.find((k) => k.startsWith("vktk:"));
  let comboType: ComboType | null = null;
  let sb: string | null = null;
  if (hasKh && !tkKey && !vktkKey) {
    comboType = "kh";
  } else if (hasKh && tkKey && !vktkKey) {
    comboType = "kh+tk";
    sb = tkKey.slice(3);
  } else if (hasKh && vktkKey && !tkKey) {
    comboType = "kh+vktk";
    sb = vktkKey.slice(5);
  }
  if (!comboType) return null;
  let beitrag = 0;
  for (const k of selKeys) beitrag += parseEur(slot.beitraege[k]);
  return { id, label, comboType, sb, beitrag };
};

const variantsForKlasse = (
  def: SammelGridKlassenDef | undefined,
  klasseIdx: number,
): Variant[] => {
  if (!def) return [];
  const num = klasseIdx + 1;
  const out: Variant[] = [];
  const p = variantFromSlot("parent", String(num), {
    selection: def.parent.selection,
    beitraege: def.parent.beitraege,
  });
  if (p) out.push(p);
  for (const sub of def.subs) {
    const v = variantFromSlot(sub.letter, `${num}${sub.letter}`, {
      selection: sub.selection,
      beitraege: sub.beitraege,
    });
    if (v) out.push(v);
  }
  return out;
};

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  hint: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.muted,
  },
  klasseCard: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", "#dcdce0"),
    ...shorthands.borderRadius("12px"),
    overflow: "hidden",
  },
  klasseHead: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "16px",
    paddingRight: "16px",
    backgroundColor: "#f1f1f3",
    ...shorthands.borderBottom("1px", "solid", "#dcdce0"),
  },
  klasseNum: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "24px",
    height: "24px",
    paddingLeft: "6px",
    paddingRight: "6px",
    backgroundColor: BRAND.purple,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 800,
    ...shorthands.borderRadius("999px"),
  },
  klasseTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    color: BRAND.ink,
    fontWeight: 600,
  },
  klasseCount: {
    marginLeft: "auto",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
  tableScroll: {
    overflowX: "auto",
  },
  table: {
    borderCollapse: "collapse",
    width: "100%",
  },
  thLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 700,
    color: BRAND.muted,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    backgroundColor: BRAND.bg,
    textAlign: "left",
    minWidth: "260px",
    position: "sticky",
    left: 0,
    zIndex: 3,
    ...shorthands.borderRight("1px", "solid", "#c9c5cf"),
  },
  thTag: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    fontWeight: 800,
    color: BRAND.ink,
    backgroundColor: BRAND.bg,
    paddingTop: "10px",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
    textAlign: "center",
    minWidth: "120px",
    ...shorthands.borderLeft("1px", "solid", "#dcdce0"),
    ...shorthands.borderTop("1px", "solid", "#dcdce0"),
  },
  thCombo: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.74rem",
    fontWeight: 700,
    color: BRAND.white,
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "6px",
    paddingRight: "6px",
    textAlign: "center",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
  },
  thComboCell: {
    backgroundColor: BRAND.bg,
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "6px",
    paddingRight: "6px",
    textAlign: "center",
    ...shorthands.borderLeft("1px", "solid", "#dcdce0"),
  },
  thSb: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    fontWeight: 600,
    color: BRAND.ink,
    backgroundColor: BRAND.bg,
    paddingTop: "6px",
    paddingBottom: "10px",
    paddingLeft: "10px",
    paddingRight: "10px",
    textAlign: "center",
    ...shorthands.borderLeft("1px", "solid", "#dcdce0"),
    ...shorthands.borderBottom("1px", "solid", "#c9c5cf"),
  },
  rowAll: {
    backgroundColor: "#f5f7fb",
  },
  cellAllLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    fontWeight: 700,
    color: BRAND.ink,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    backgroundColor: "#f5f7fb",
    position: "sticky",
    left: 0,
    zIndex: 2,
    ...shorthands.borderRight("1px", "solid", "#c9c5cf"),
    ...shorthands.borderTop("1px", "solid", "#dcdce0"),
  },
  cellVehicleLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    backgroundColor: BRAND.white,
    position: "sticky",
    left: 0,
    zIndex: 2,
    ...shorthands.borderRight("1px", "solid", "#c9c5cf"),
    ...shorthands.borderTop("1px", "solid", "#dcdce0"),
  },
  plate: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    fontWeight: 600,
    color: BRAND.ink,
    display: "block",
  },
  vehicleSub: {
    fontSize: "0.74rem",
    color: BRAND.muted,
    display: "block",
    marginTop: "2px",
  },
  cell: {
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "10px",
    paddingRight: "10px",
    textAlign: "center",
    cursor: "pointer",
    ...shorthands.borderTop("1px", "solid", "#dcdce0"),
    ...shorthands.borderLeft("1px", "solid", "#dcdce0"),
    transitionProperty: "background-color",
    transitionDuration: "120ms",
    ":hover": { backgroundColor: "rgba(61,139,217,0.06)" },
  },
  cellSelected: {
    backgroundColor: "rgba(61,139,217,0.10)",
    fontWeight: 700,
  },
  radioWrap: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    cursor: "pointer",
  },
  radio: {
    width: "16px",
    height: "16px",
    accentColor: "#3D8BD9",
    cursor: "pointer",
    margin: 0,
  },
  beitragLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
  },
  beitragGesamt: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    color: BRAND.ink,
    fontWeight: 700,
  },
  empty: {
    paddingTop: "32px",
    paddingBottom: "32px",
    textAlign: "center",
    color: BRAND.muted,
    fontStyle: "italic",
    fontSize: "0.92rem",
  },
});

type Props = {
  /** Klassen-Definition aus dem Vereinbarungs-Schritt. */
  klassenDefinition: SammelGridState;
  vehicles: RiskSummary[];
  /** riskId → KlasseKey. */
  klasseByRisk: Record<number, KlasseKey | null>;
  /** riskId → variant id ("parent" | sub.letter). */
  assignment: Record<number, string>;
  onChange: (next: Record<number, string>) => void;
  readOnly?: boolean;
};

const UmfangAssignmentTable = ({
  klassenDefinition,
  vehicles,
  klasseByRisk,
  assignment,
  onChange,
  readOnly = false,
}: Props) => {
  const styles = useStyles();

  // Group vehicles by klasse.
  const grouped = SAMMELGRID_KLASSEN.map((klasse, idx) => {
    const vs = vehicles.filter((v) => klasseByRisk[v.id] === klasse);
    if (vs.length === 0) return null;
    const def = klassenDefinition.klassen?.[klasse];
    const variants = variantsForKlasse(def, idx);
    return { klasse, idx, num: idx + 1, vehicles: vs, variants };
  }).filter((x): x is NonNullable<typeof x> => !!x);

  if (vehicles.length === 0) {
    return (
      <div className={styles.empty}>
        Keine Risiken ausgewählt — bitte zuerst Risiken im Abschnitt
        „Risikoinformationen" auswählen.
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.hint}>
        Pro Fahrzeug eine Variante aus der Klassen-Definition wählen.
        „Für alle Fahrzeuge" überträgt die Auswahl auf alle Fahrzeuge dieser Klasse.
      </div>
      {grouped.map(({ klasse, num, vehicles: vs, variants }) => {
        if (variants.length === 0) {
          return (
            <div key={klasse} className={styles.klasseCard}>
              <div className={styles.klasseHead}>
                <span className={styles.klasseNum}>{num}</span>
                <span className={styles.klasseTitle}>{KLASSEN_LABELS[klasse]}</span>
                <span className={styles.klasseCount}>
                  {vs.length} Fahrzeug{vs.length === 1 ? "" : "e"}
                </span>
              </div>
              <div className={styles.empty}>
                Keine gültige Varianten in der Klassen-Definition — bitte zuerst
                in der Vereinbarung Deckungen für „{KLASSEN_LABELS[klasse]}" anlegen.
              </div>
            </div>
          );
        }
        // Did the user set every vehicle to the same variant?
        const allEqual =
          vs.length > 0 &&
          variants.some((v) => vs.every((veh) => assignment[veh.id] === v.id));
        const allSelectedVariantId = allEqual
          ? variants.find((v) => vs.every((veh) => assignment[veh.id] === v.id))!.id
          : null;
        const setAllTo = (variantId: string) => {
          const next = { ...assignment };
          for (const veh of vs) next[veh.id] = variantId;
          onChange(next);
        };
        const setOneTo = (riskId: number, variantId: string) => {
          onChange({ ...assignment, [riskId]: variantId });
        };

        return (
          <div key={klasse} className={styles.klasseCard}>
            <div className={styles.klasseHead}>
              <span className={styles.klasseNum}>{num}</span>
              <span className={styles.klasseTitle}>{KLASSEN_LABELS[klasse]}</span>
              <span className={styles.klasseCount}>
                {vs.length} Fahrzeug{vs.length === 1 ? "" : "e"}
              </span>
            </div>
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.thLabel}>{KLASSEN_LABELS[klasse]}</th>
                    {variants.map((v) => (
                      <th key={`tag-${v.id}`} className={styles.thTag}>
                        {v.label}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    <th
                      className={styles.thLabel}
                      style={{ paddingTop: 4, paddingBottom: 4 }}
                    >
                      Deckung
                    </th>
                    {variants.map((v) => (
                      <td key={`combo-${v.id}`} className={styles.thComboCell}>
                        <span
                          className={styles.thCombo}
                          style={{ backgroundColor: COMBO_COLOR[v.comboType], display: "inline-block", paddingLeft: 10, paddingRight: 10, borderRadius: 999 }}
                        >
                          {COMBO_LABEL[v.comboType]}
                        </span>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <th
                      className={styles.thLabel}
                      style={{ paddingTop: 4, paddingBottom: 8 }}
                    >
                      Selbstbeteiligung
                    </th>
                    {variants.map((v) => (
                      <th key={`sb-${v.id}`} className={styles.thSb}>
                        {v.sb ?? "—"}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Für alle Fahrzeuge */}
                  <tr className={styles.rowAll}>
                    <td className={styles.cellAllLabel}>Für alle Fahrzeuge</td>
                    {variants.map((v) => {
                      const isSelected = allSelectedVariantId === v.id;
                      const total = v.beitrag * vs.length;
                      return (
                        <td
                          key={`all-${v.id}`}
                          className={mergeClasses(styles.cell, isSelected && styles.cellSelected)}
                          onClick={() => !readOnly && setAllTo(v.id)}
                        >
                          <label className={styles.radioWrap} onClick={(e) => e.stopPropagation()}>
                            <input
                              type="radio"
                              className={styles.radio}
                              name={`all-${klasse}`}
                              checked={isSelected}
                              disabled={readOnly}
                              onChange={() => setAllTo(v.id)}
                            />
                            <span className={styles.beitragGesamt}>
                              {formatEur(total)}
                            </span>
                          </label>
                        </td>
                      );
                    })}
                  </tr>
                  {/* Pro Fahrzeug */}
                  {vs.map((veh) => {
                    const plate = veh.characteristics.licensePlate || `Risiko ${veh.id}`;
                    const vin = veh.characteristics.vin;
                    const subId = vin ? `VIN ${vin}` : `Risiko-ID ${veh.id}`;
                    const selectedId = assignment[veh.id];
                    return (
                      <tr key={veh.id}>
                        <td className={styles.cellVehicleLabel}>
                          <span className={styles.plate}>{plate}</span>
                          <span className={styles.vehicleSub}>{subId}</span>
                        </td>
                        {variants.map((v) => {
                          const isSelected = selectedId === v.id;
                          return (
                            <td
                              key={`${veh.id}-${v.id}`}
                              className={mergeClasses(styles.cell, isSelected && styles.cellSelected)}
                              onClick={() => !readOnly && setOneTo(veh.id, v.id)}
                            >
                              <label className={styles.radioWrap} onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="radio"
                                  className={styles.radio}
                                  name={`veh-${veh.id}`}
                                  checked={isSelected}
                                  disabled={readOnly}
                                  onChange={() => setOneTo(veh.id, v.id)}
                                />
                                <span className={styles.beitragLabel}>
                                  {formatEur(v.beitrag)}
                                </span>
                              </label>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default UmfangAssignmentTable;
